import JetFontIcon, {
  JET_FONTICON_ALIASES,
} from '@src/shared/components/JetFontIcon';
import { IOrder, OrderStatus, useOrdersState } from '../../context/orders';
import useStyles from './styles';
import React, { FC, useEffect, useState } from 'react';
import { Typography, useTheme } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { getTimeFromDate } from '@src/shared/utils';
import CurrencyCodeIcon, {
  AvailableCurrencyCode,
} from '@src/shared/components/CurrencyCode';
import { useTranslation } from 'react-i18next';
import { OrderDetailCardItems } from '../OrderDetailCardItems';
import Note, { NoteType } from '@src/shared/components/Note';
import DOMPurify from 'dompurify';
import useGetFeedback from '@src/shared/hooks/use-getfeedback';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';

const OrderDetailCard: FC<IOrder> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const {triggerFeedbackCampaign} = useGetFeedback();
  const [cardLoader, setCardLoader] = useState<boolean>(true);
  const { restaurant } = useKeycloakUserState();
  const { clickedOrderId } = useOrdersState();

  useEffect(() => {
    setTimeout(() => {
      setCardLoader(false);
      triggerFeedbackCampaign('mcp_users',['de','fr','nl','pl','bg','ro','dk'],restaurant?.country)
    }, 500);

    return () => setCardLoader(true);
  }, [clickedOrderId]);

  const OrderDetailLoaderWrapper = ({ children, amount }: any): JSX.Element => {
    return cardLoader ? (
      amount ? (
        <>
          {[...Array(amount)].map((_, i) => (
            <Skeleton
              width="100%"
              height={20}
              variant="text"
              animation="wave"
              className={classes.skeletonLoader}
              key={i}
            ></Skeleton>
          ))}
        </>
      ) : (
        <Skeleton
          width="100%"
          height={20}
          variant="text"
          animation="wave"
          className={classes.skeletonLoader}
        />
      )
    ) : (
      <>{children}</>
    );
  };

  return (
    <div className={classes.orderDetailCardWrapper}>
      <ul className={classes.orderDetailCardDetailItem}>
        <li>
          <span className={classes.orderDetailIcons}>
            <JetFontIcon
              iconname={JET_FONTICON_ALIASES.ORDER}
              size={20}
              color={theme.palette.jet?.contentBrand}
            />
          </span>
          <OrderDetailLoaderWrapper>
            <Typography
              variant="body1"
              className="body_small_regular"
              align="left"
            >
              {props?.reference}
            </Typography>
            {props?.hasAlcohol ? (
              <Note
                note="order.alcohol"
                icon={{
                  name: JET_FONTICON_ALIASES.ALCOHOL,
                  color: theme?.palette?.jet?.contentDefault as string,
                  size: 12,
                }}
                type={NoteType.Warning}
              />
            ) : null}
          </OrderDetailLoaderWrapper>
        </li>
        <li>
          <span className={classes.orderDetailIcons}>
            <JetFontIcon
              iconname={JET_FONTICON_ALIASES.LOCATION_PIN}
              size={20}
              color={theme.palette.jet?.contentBrand}
            />
          </span>
          <div className={classes.orderDetailAddress}>
            <OrderDetailLoaderWrapper>
              <Typography
                variant="body1"
                className="body_small_regular"
                align="left"
              >
                {props?.customer?.address?.street}{' '}
                {props?.customer?.address?.streetNumber}
              </Typography>
              <Typography variant="caption" className="caption">
                {props?.customer?.address?.city}
              </Typography>
            </OrderDetailLoaderWrapper>
          </div>
        </li>
        <li>
          <span className={classes.orderDetailIcons}>
            <JetFontIcon
              iconname={JET_FONTICON_ALIASES.STOPWATCH}
              size={20}
              color={theme.palette.jet?.contentBrand}
            />
          </span>
          <OrderDetailLoaderWrapper>
            <Typography
              variant="body1"
              className="body_small_regular"
              align="left"
            >
              {props?.status !== OrderStatus.Delivered ? 'ETA ' : null}

              {props?.expectedDeliveryTime ? (
                getTimeFromDate(
                  props?.status === OrderStatus.Delivered
                    ? props?.updateDate
                    : (props.expectedDeliveryTime as any)
                )
              ) : (
                <>
                  {props?.status === OrderStatus.Delivered && props?.updateDate
                    ? getTimeFromDate(props?.updateDate)
                    : 'TBA'}
                </>
              )}
            </Typography>
          </OrderDetailLoaderWrapper>
        </li>
        <li>
          <span className={classes.orderDetailIcons}>
            <JetFontIcon
              iconname={JET_FONTICON_ALIASES.USER}
              size={18}
              color={theme.palette.jet?.contentBrand}
            />
          </span>
          <div className={classes.orderDetailAddress}>
            <OrderDetailLoaderWrapper>
              <Typography
                variant="body1"
                className="body_small_bold"
                align="left"
              >
                {props?.customer?.lastName}
              </Typography>
            </OrderDetailLoaderWrapper>
          </div>
        </li>
        <li>
          <span className={classes.orderDetailIcons}>
            <JetFontIcon
              iconname={JET_FONTICON_ALIASES.CASH}
              size={15}
              color={theme.palette.jet?.contentBrand}
            />
          </span>
          <OrderDetailLoaderWrapper>
            <Typography
              variant="body1"
              className="body_small_regular"
              align="left"
            >
              {t(`order.payment_${props?.payment?.status}`)}{' '}
            </Typography>
          </OrderDetailLoaderWrapper>
        </li>
        {props.comments && props.comments.length && <li>
          <span className={classes.orderDetailIcons}>
            <JetFontIcon
              iconname={JET_FONTICON_ALIASES.INFO_CIRCLE}
              size={20}
              color={theme.palette.jet?.contentBrand}
            />
          </span>
          
          <div className={classes.orderDetailComment}>
            <OrderDetailLoaderWrapper>
              <Typography
                variant="body1"
                className="body_small_regular"
                align="left"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(props?.comments,{})}}
              />
            </OrderDetailLoaderWrapper>
          </div>
        </li> || <></>}
      </ul>
      <div className={classes.orderDetailItemsWrapper}>
        <OrderDetailLoaderWrapper amount={3}>
          {props?.items?.map((item) => {
            return (
              <div key={item.id}>
                <OrderDetailCardItems {...item} />
              </div>
            );
          })}
        </OrderDetailLoaderWrapper>
      </div>
    </div>
  );
};

export default OrderDetailCard;
